"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function () {
  const stages = [{
    number: "01",
    label: "— ORDER PLACED",
    title: "Your style / is secured.",
    description: "We have received your order with excitement. Our team is carefully reviewing your selections to ensure every piece meets our premium quality standards.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/order_placed_confirmation.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/order_placed_confirmation.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/order_placed_confirmation.webp-wvc-sizes",
    alt: "Digital order confirmation screen on a smartphone",
    side: "right"
  }, {
    number: "02",
    label: "— PROCESSING",
    title: "Crafting / your look.",
    description: "Our artisans are hand-picking your items from our boutique collection. Each garment is inspected, steamed, and folded with meticulous care.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/order_processing_boutique.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/order_processing_boutique.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/order_processing_boutique.webp-wvc-sizes",
    alt: "Fashion boutique interior with organized clothing racks",
    side: "left"
  }, {
    number: "03",
    label: "— DISPATCHED",
    title: "On its way / to you.",
    description: "Your package has left our studio. It is currently in transit, moving swiftly through our premium logistics network to reach your doorstep.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/order_dispatched_transit.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/order_dispatched_transit.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/order_dispatched_transit.webp-wvc-sizes",
    alt: "Parcel being handled by a professional courier service",
    side: "right"
  }, {
    number: "04",
    label: "— DELIVERED",
    title: "Style / arrived.",
    description: "Your Lush Fashion order has been delivered. We hope your new pieces bring confidence and elegance to your wardrobe. Enjoy your new look.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/order_delivered_package.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/order_delivered_package.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/order_delivered_package.png-wvc-sizes",
    alt: "Elegant shopping bag placed on a doorstep",
    side: "left"
  }];
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="4583" id="ordertrackingtimeline" className="bg-background text-foreground py-40 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {}
        <div ref={header.ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-32 transition-all duration-700 ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-7">
            <p className="font-sans text-xs tracking-widest text-muted-foreground mb-8 uppercase">
              — Order Journey
            </p>
            <h1 className="font-default font-semibold text-foreground leading-[0.95] tracking-tight" style={{
            fontSize: "clamp(3rem, 7vw, 6rem)"
          }}>
              Tracking your /{" "}
              <span className="italic text-accent">elegance.</span>
            </h1>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 flex items-end">
            <p className="font-default italic text-lg lg:text-xl text-muted-foreground leading-relaxed">
              At Lush Fashion, every delivery is a promise kept. Follow the journey of your curated pieces from our studio to your home with complete transparency.
            </p>
          </div>
        </div>

        {}
        <div className="relative">
          {}
          <ProgressiveSpine />

          <div className="relative space-y-20 lg:space-y-32">
            {stages.map((stage, i) => <Stage key={i} stage={stage} data-index={i} />)}
          </div>
        </div>
      </div>
    </section>;
}
function ProgressiveSpine() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05,
    once: true
  });
  return <div ref={ref} className="absolute left-4 sm:left-8 lg:left-1/3 top-0 bottom-0 w-px pointer-events-none" aria-hidden="true">
      <div className="relative h-full w-px bg-border/40">
        <div className="absolute top-0 left-0 w-px bg-accent transition-all ease-out" style={{
        height: isVisible ? "100%" : "0%",
        transitionDuration: "1800ms"
      }} />
      </div>
    </div>;
}
type StageProps = {
  stage: {
    number: string;
    label: string;
    title: string;
    description: string;
    image: string;
    srcset: string;
    sizes: string;
    alt: string;
    side: string;
  };
  "data-index"?: number;
};
function Stage({
  stage,
  ...rest
}: StageProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.25
  });
  const isRight = stage.side === "right";
  return <div ref={ref} {...rest} className="relative grid grid-cols-12 gap-4 sm:gap-6 lg:gap-8 items-start">
      {}
      <div className={`absolute left-4 sm:left-8 lg:left-1/3 top-0 -translate-x-1/2 flex flex-col items-center transition-opacity duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`}>
        <span className={`block size-2 rounded-lg transition-all duration-1000 ${isVisible ? "bg-accent scale-100 shadow-md" : "bg-border scale-75"}`} />
        <span className="font-default text-foreground/80 text-xs sm:text-sm mt-3 -ml-0">
          {stage.number}
        </span>
      </div>

      {}
      {isRight ? <>
          {}
          <div className="hidden lg:block lg:col-span-4" />
          {}
          <div className="col-span-12 pl-12 sm:pl-16 lg:pl-0 lg:col-span-8">
            <StageContent stage={stage} isVisible={isVisible} alignImage="right" />
          </div>
        </> : <>
          {}
          <div className="col-span-12 pl-12 sm:pl-16 lg:pl-12 lg:col-span-7">
            <StageContent stage={stage} isVisible={isVisible} alignImage="left" />
          </div>
          {}
          <div className="hidden lg:block lg:col-span-5" />
        </>}
    </div>;
}
function StageContent({
  stage,
  isVisible,
  alignImage
}: {
  stage: {
    number: string;
    label: string;
    title: string;
    description: string;
    image: string;
    srcset: string;
    sizes: string;
    alt: string;
  };
  isVisible: boolean;
  alignImage: string;
}) {
  return <div className={`flex flex-col gap-8 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      <div className={`flex flex-col lg:flex-row gap-8 lg:gap-12 ${alignImage === "left" ? "lg:flex-row-reverse" : ""}`}>
        {}
        <div className="flex-1 max-w-xl">
          <p className="font-sans text-xs tracking-widest text-accent mb-6 uppercase">
            {stage.label}
          </p>
          <h3 className="font-default font-semibold text-foreground leading-[1.05] tracking-tight mb-6" style={{
          fontSize: "clamp(2.5rem, 5vw, 4rem)"
        }}>
            {stage.title.split("/").map((part, idx) => <span key={idx} className="block">
                {part.trim()}
                {idx === 0 && stage.title.includes("/") && <span className="text-accent/40"> /</span>}
              </span>)}
          </h3>
          <p className="font-default italic text-base sm:text-lg text-muted-foreground leading-relaxed max-w-md">
            {stage.description}
          </p>
        </div>

        {}
        <div className={`shrink-0 ${alignImage === "left" ? "lg:-translate-x-4 lg:-translate-y-4" : "lg:translate-x-4 lg:translate-y-8"}`}>
          <div className="overflow-hidden rounded-lg shadow-md w-44 h-56 sm:w-56 sm:h-72">
            <img src={stage.image} data-wvc-srcset={stage.srcset} data-wvc-sizes={stage.sizes} alt={stage.alt} loading="lazy" decoding="async" className={`w-full h-full object-cover transition-transform duration-[1400ms] ${isVisible ? "scale-100" : "scale-110"}`} />
          </div>
        </div>
      </div>
    </div>;
}